<template>
  <div class="order-page">
    <template v-if="!restrictedBrand">
      <!-- Main Header -->
      <div class="main-header">
        <ulu-order-header :order="order" :customer="order.customer" :refreshOrder="fetchOrder" />
        <button class="refresh-btn" @click="fetchOrder" :disabled="getShowLoadingModal">
          <icon name="sync-alt" :spin="getShowLoadingModal"></icon>
        </button>
      </div>

      <!-- Main Content Layout -->
      <div class="order-content">
        <!-- Left Sidebar -->
        <div class="order-sidebar">
          <!-- Order and Inquiry Numbers -->
          <div class="order-inquiry-container">
            <h2 class="order-number">
              {{ $t("globals.order").toUpperCase() + " " + order.order_no }}
            </h2>
            <div v-if="order.inquiry" class="inquiry-number">
              <router-link
                class="inquiry-link"
                :to="{ name: 'inquiry', params: { id: order.inquiry.id } }"
              >
                {{ $t("globals.inquiry") + " " + order.inquiry.inquiry_no }}
              </router-link>
            </div>
          </div>

          <!-- Order Details -->
          <ulu-order-details :order="order" />
        </div>

        <!-- Main Content Area -->
        <div class="order-main">
          <div class="tabs-wrapper">
            <b-tabs lazy>
              <!-- Items / settings tab -->
              <b-tab :title="$t('globals.items')">
                <ulu-order-items-tab :order="order" :refresh="fetchOrder" />
              </b-tab>
              <!-- Invoices tab -->
              <b-tab v-if="getUserBrandPermissions.invoices_index" :title="$t('globals.invoices')">
                <ulu-order-invoices-tab :refreshOrder="fetchOrder" :currency="order.currency"
                  :invoiceSchedules="order.invoice_schedules" :invoices="order.invoices" />
              </b-tab>
              <!-- Devices old tab -->
              <b-tab v-if="order.order_devices.length > 0" title="Dongels (old)">
                <ulu-order-devices-tab-old :status="order.status" :refreshOrder="fetchOrder" :devices="order.order_devices" />
              </b-tab>
              <b-tab :title="$t('globals.units')">
                <ulu-order-devices-tab :status="order.status" :refreshOrder="fetchOrder" :devices="order.devices" />
              </b-tab>
              <!-- Order services tab -->
              <b-tab v-if="getUserBrandPermissions.order_service_tab" :title="$t('globals.services_services')">
                <ulu-order-services-tab :refreshOrder="fetchOrder" :order="order"
                  :contactAllInfo="order.customer.contact_all_info" />
              </b-tab>
              <!-- Synatec orders tab -->
              <b-tab v-if="getUserBrandPermissions.synatec_orders_index" title="Sinatec">
                <ulu-order-synatec-tab :refreshOrder="fetchOrder" :order="order" />
              </b-tab>
              <!-- History tab -->
              <b-tab :title="$t('globals.history')">
                <ulu-order-history-tab />
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";

import systemRestriction from "@/mixins/systemRestriction";

import "vue-awesome/icons/sync-alt";

export default {
  mixins: [systemRestriction],
  systemRestrictionReload: "fetchOrder",
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue"),
    "ulu-order-header": () =>
      import("@/components/orders/order/order_header/OrderHeader"),
    "ulu-order-details": () => import("@/components/orders/order/OrderDetails"),
    "ulu-order-items-tab": () =>
      import("@/components/orders/order/order_items_tab/OrderItemsTab"),
    "ulu-order-devices-tab": () =>
      import("@/components/orders/order/order_devices_tab/OrderDevicesTab"),
    "ulu-order-devices-tab-old": () =>
      import("@/components/orders/order/order_devices_tab_old/OrderDevicesTab"),
    "ulu-order-services-tab": () =>
      import("@/components/orders/order/order_services_tab/OrderServicesTab"),
    "ulu-order-invoices-tab": () =>
      import("@/components/orders/order/order_invoices_tab/OrderInvoicesTab"),
    "ulu-order-synatec-tab": () =>
      import("@/components/orders/order/order_synatec_tab/OrderSynatecTab"),
    "ulu-order-history-tab": () =>
      import("@/components/orders/order/order_history_tab/OrderHistoryTab")
  },
  data() {
    return {
      order: {
        order_no: 0,
        agent: {
          full_name: ""
        },
        settings: [],
        contract_duration: 0,
        created_at: "",
        send_invoices: false,
        retroactive_invoicing: false,
        customer: {
          id: 0,
          company_name: "",
          company_country: "",
          contact_all_info: ""
        },
        inquiry: {
          id: 0,
          inquiry_no: 0
        },
        invoices: [],
        invoice_schedules: [],
        services: [],
        order_devices: []
      }
    };
  },
  methods: {
    ...mapMutations(["setShowLoadingModal"]),
    ...mapActions("axios_mod", ["simpleFetch"]),
    async fetchOrder() {
      // if /orders/:id is called directly, we need to fetch the order
      let order_id = parseInt(this.$route.params.id);

      // if /customers/:id we need to find the first order of the customer
      if (this.$route.name == "customer") {
        const data = {
          query: `
            query {
              Customer (id: ${parseInt(this.$route.params.id)}) {
                first_order {
                  id
                }
              }
            }
          `
        };
        await this.simpleFetch({ data, this_ref: this }).then(res => {
          if (res) {
            order_id = res.Customer.first_order.id;
          }
        });
      }

      const data = {
        query: `
          query {
            order (id: ${parseInt(order_id)}) {
              agent {
                full_name
              }
              currency
              order_no
              brand
              notes
              status
              contract_start_on
              created_at
              contract_duration
              payment_term_name
              payment_term
              payment_type
              total_price_ex_vat
              total_price_with_vat
              available_installations
              cancell_at
              cancell_type
              send_invoices
              retroactive_invoicing
              order_devices {
                id
                imei
                status
              }
              devices {
                id
                imei
                status
                product
                batch
                selected: default_false
                pending_termination{
                  id
                  terminate_at
                  return_process
                }
              }
              inquiry {
                id
                inquiry_no
                company{
                  name
                }
              }
              settings {
                key
                value
              }
              services {
                id
                agent {
                  full_name
                }
                service_type
                fieldtracker_task_name
                assembly_type
                status
                created_at
                warranty
                needs_to_be_billed
                _rowVariant
              }
              synatec_orders {
                id
                agent {
                  full_name
                }
                status
                created_at
                _rowVariant
              }
              invoices {
                id
                invoice_no
                total_price
                total_price_ex_vat
                created_at
                sent_at
                sent_to
                is_paid
                _rowVariant
              }
              customer {
                id
                company_name
                company_country
                contact_first_name
                contact_last_name
                contact_email
                contact_all_info

                shipping_first_name
                shipping_last_name
                shipping_email
                shipping_phone
                shipping_address
                shipping_city
                shipping_postal_code
                shipping_country
              }
              order_items {
                id
                item_name
                price_one_time
                price_per_month
                discount
                quantity
                vat
                synch_units_on_day_of_month
                _showDetails: default_true
                billing_item {
                  synatec_products {
                    id
                    name
                    synatec_code
                  }
                }
              }
              invoice_schedules {
                id
                schedule_at
                order_id
                status
                total_price
                _rowVariant
              }
            }
          }
        `
      };
      this.setShowLoadingModal(true);
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res && this.checkSystem(res.order.brand)) {
            this.order = res.order;
            this.order.invoice_schedules = res.order.invoice_schedules
              .filter(i => i.status != "created" && i.status != "created_plus")
          }
        })
        .finally(() => {
          this.setShowLoadingModal(false);
        });
    }
  },
  computed: {
    ...mapGetters(["getUserBrandPermissions", "getShowLoadingModal"])
  },
  created() {
    if (!this.getUserBrandPermissions.order_show) {
      this.$router.push({ name: "dashboard" });
    }
  },
  mounted() {
    this.fetchOrder();
  }
};
</script>

<style scoped>
.order-page {
  background-color: #f7f7f7;
  min-height: 100vh;
  padding: 24px;
}

/* Main Header */
.main-header {
  background: white;
  padding: 16px 24px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.refresh-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  color: #4a5568;
  transition: all 0.2s;
  flex-shrink: 0;
}

.refresh-btn:hover:not(:disabled) {
  background-color: #edf2f7;
  border-color: #5469d4;
  color: #5469d4;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Content Layout */
.order-content {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 24px;
}

.order-sidebar {
  background: white;
  padding: 20px;
  height: fit-content;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Order and Inquiry Numbers in Sidebar */
.order-inquiry-container {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.order-number {
  font-size: 18px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 6px 0;
  line-height: 1.3;
}

.inquiry-number {
  font-size: 14px;
  color: #6c757d;
  margin-top: 6px;
}

.inquiry-link {
  color: #17a2b8;
  text-decoration: none;
  font-weight: 500;
}

.inquiry-link:hover {
  text-decoration: underline;
}

.order-main {
  background: white;
  overflow: visible;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tabs-wrapper >>> .nav-tabs {
  border-bottom: 1px solid #e0e0e0;
  background-color: white;
  padding: 0;
  margin-bottom: 0;
}

.tabs-wrapper >>> .nav-tabs .nav-link {
  border: none;
  border-bottom: 3px solid transparent;
  color: #6c757d;
  font-size: 0.9375rem;
  font-weight: 500;
  padding: 1rem 1.5rem;
  background-color: transparent;
  transition: all 0.2s;
}

.tabs-wrapper >>> .nav-tabs .nav-link:hover {
  border-bottom-color: #ff6b35;
  color: #495057;
}

.tabs-wrapper >>> .nav-tabs .nav-link.active {
  color: #ff6b35;
  border-bottom-color: #ff6b35;
  background-color: white;
  font-weight: 600;
}

.tabs-wrapper >>> .tab-content {
  background-color: white;
  border: none;
  padding: 24px;
}

/* Responsive table wrapper for all tabs */
.tabs-wrapper >>> .table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 16px;
}

.tabs-wrapper >>> .modern-table {
  min-width: 600px;
  width: 100%;
}

@media (max-width: 1024px) {
  .main-header {
    flex-wrap: wrap;
  }

  .order-content {
    grid-template-columns: 1fr;
  }

  .order-sidebar {
    order: 2;
  }

  .order-main {
    order: 1;
  }
}

@media (max-width: 768px) {
  .order-page {
    padding: 16px;
  }

  .main-header {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
  }

  .order-number {
    font-size: 16px;
  }

  .tabs-wrapper >>> .nav-tabs .nav-link {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
  }

  .tabs-wrapper >>> .tab-content {
    padding: 16px;
  }
}
</style>
